import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { CheckCircle, Users, HelpCircle, Crown, ArrowRight, X as XIcon } from 'lucide-react';
import * as Icons from 'lucide-react';
import { API_URLS } from '../../config/api';
import { parsePrice, formatPriceWithoutCents } from '../../utils/price';
import MediaDisplay from '../MediaDisplay';

const fallbackPlans = [
  {
    code: 'RS_SMALL',
    name: 'RS Small',
    price: '$12.99',
    original: '$22.99',
    features: [
      '25 Web Sitesi',
      '40 GB SSD Disk Alanı',
      '1 Core CPU',
      '2048 MB RAM',
      'Sınırsız Trafik',
      'Sınırsız E-Posta',
      'Sınırsız MySQL',
      'Plesk Panel'
    ]
  },
  {
    code: 'RS_MEDIUM',
    name: 'RS Medium',
    price: '$15.99',
    original: '$32.99',
    featured: true,
    features: [
      '50 Web Sitesi',
      '60 GB SSD Disk Alanı',
      '1 Core CPU',
      '3072 MB RAM',
      'Sınırsız Trafik',
      'Sınırsız E-Posta',
      'Sınırsız MySQL',
      'Plesk Panel'
    ]
  },
  {
    code: 'RS_LARGE',
    name: 'RS Large',
    price: '$24.99',
    original: '$51.99',
    features: [
      '100 Web Sitesi',
      '120 GB SSD Disk Alanı',
      '2 Core CPU',
      '4096 MB RAM',
      'Sınırsız Trafik',
      'Sınırsız E-Posta',
      'Sınırsız MySQL',
      'Plesk Panel'
    ]
  },
  {
    code: 'RS_XLARGE',
    name: 'RS XLarge',
    price: '$36.99',
    original: '$76.99',
    features: [
      '200 Web Sitesi',
      '250 GB SSD Disk Alanı',
      '4 Core CPU',
      '6144 MB RAM',
      'Sınırsız Trafik',
      'Sınırsız E-Posta',
      'Sınırsız MySQL',
      'Plesk Panel'
    ]
  }
];

const fallbackFaqs = [
  {
    q: 'Reseller (Bayi) Hosting nedir?',
    a: 'Kiralanan web alanını farklı müşterilere paylaştırmanıza ve yönetmenize imkan veren hosting türüdür. Size özel bayi paneli ile paket ve kaynak ataması yapabilirsiniz.'
  },
  {
    q: 'Kimler için uygundur?',
    a: 'Birden çok site yöneten ajanslar, freelance geliştiriciler, tasarımcılar ve barındırma hizmeti vermek isteyenler için idealdir.'
  },
  {
    q: 'Hangi kontrol paneli?',
    a: 'Plesk bayi kontrol paneli ile kolay yönetim, kaynak limitleme ve müşteri hesap işlemleri yapılabilir.'
  }
];

const resolveIcon = (iconName, fallback = 'CheckCircle') => {
  if (!iconName && Icons[fallback]) return Icons[fallback];
  if (iconName && Icons[iconName]) return Icons[iconName];
  if (fallback && Icons[fallback]) return Icons[fallback];
  return CheckCircle;
};

const ResellerHostingPage = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);

  const [faqs, setFaqs] = useState([]);
  const [faqsLoading, setFaqsLoading] = useState(true);
  
  const [whatsappNumber, setWhatsappNumber] = useState('905324404011');
  const [contentReady, setContentReady] = useState(false);
  const [pageContent, setPageContent] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setContentReady(true);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchPackages();
    fetchFaqs();
    fetchSettings();
    fetchContent();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${API_URLS.ADMIN.ADMIN_API}?action=getCompanySettings`);
      if (res.ok) {
        const json = await res.json();
        if (json.status === 'success' && json.data?.whatsapp) {
          setWhatsappNumber(json.data.whatsapp);
        }
      }
    } catch (e) {
      console.error('WhatsApp numarası alınamadı:', e);
    }
  };

  const fetchContent = async () => {
    try {
      const res = await fetch(API_URLS.PAGE_CONTENT('bayi-hosting'));
      const data = await res.json();

      if (data.status === 'success' && data.data) {
        const additional =
          typeof data.data.additional_sections === 'string'
            ? JSON.parse(data.data.additional_sections || '{}')
            : data.data.additional_sections || {};
        setPageContent({
          ...data.data,
          additional_sections: additional
        });
      } else {
        setPageContent(null);
      }
    } catch (error) {
      console.error('Bayi hosting içeriği yüklenemedi:', error);
      setPageContent(null);
    }
  };

  const derivedContent = useMemo(() => {
    const additionalRaw = pageContent?.additional_sections;
    const additional =
      additionalRaw && typeof additionalRaw === 'object' && !Array.isArray(additionalRaw)
        ? additionalRaw
        : {};

    const heroAdditional =
      additional.hero && typeof additional.hero === 'object' && !Array.isArray(additional.hero)
        ? additional.hero
        : {};
    const packagesSection =
      additional.packages_section &&
      typeof additional.packages_section === 'object' &&
      !Array.isArray(additional.packages_section)
        ? additional.packages_section
        : {};
    const experienceSection =
      additional.experience_section &&
      typeof additional.experience_section === 'object' &&
      !Array.isArray(additional.experience_section)
        ? additional.experience_section
        : {};
    const highlightsSection =
      additional.highlights_section &&
      typeof additional.highlights_section === 'object' &&
      !Array.isArray(additional.highlights_section)
        ? additional.highlights_section
        : {};
    const managementSection =
      additional.management_section &&
      typeof additional.management_section === 'object' &&
      !Array.isArray(additional.management_section)
        ? additional.management_section
        : {};
    const ctaSection =
      additional.cta_section &&
      typeof additional.cta_section === 'object' &&
      !Array.isArray(additional.cta_section)
        ? additional.cta_section
        : {};

    return {
      hero: {
        badge: pageContent?.hero_badge_text ?? '',
        title: pageContent?.hero_title ?? '',
        description: pageContent?.hero_description ?? '',
        image_url: heroAdditional.image_url || pageContent?.hero_image_url || '',
        bullets: Array.isArray(heroAdditional.bullets) ? heroAdditional.bullets : []
      },
      packages_section: {
        title: packagesSection.title || '',
        description: packagesSection.description || '',
        note: packagesSection.note || ''
      },
      experience_section: {
        title: experienceSection.title || '',
        description: experienceSection.description || '',
        cards: Array.isArray(experienceSection.cards) ? experienceSection.cards : []
      },
      highlights_section: {
        title: highlightsSection.title || '',
        subtitle: highlightsSection.subtitle || '',
        cards: Array.isArray(highlightsSection.cards) ? highlightsSection.cards : []
      },
      management_section: {
        title: managementSection.title || '',
        description: managementSection.description || '',
        cards: Array.isArray(managementSection.cards) ? managementSection.cards : []
      },
      cta_section: {
        title: ctaSection.title || '',
        description: ctaSection.description || '',
        primary_button:
          ctaSection.primary_button && typeof ctaSection.primary_button === 'object'
            ? ctaSection.primary_button
            : null,
        secondary_button:
          ctaSection.secondary_button && typeof ctaSection.secondary_button === 'object'
            ? ctaSection.secondary_button
            : null,
        highlights: Array.isArray(ctaSection.highlights) ? ctaSection.highlights : []
      }
    };
  }, [pageContent]);

  const heroBullets = useMemo(() => {
    const rawBullets = Array.isArray(derivedContent.hero.bullets)
      ? derivedContent.hero.bullets
      : [];
    return rawBullets
      .map((bullet) => {
        if (typeof bullet === 'string') {
          const text = bullet.trim();
          return text ? { text, icon: undefined } : null;
        }
        if (bullet && typeof bullet === 'object') {
          const text = (bullet.text || bullet.title || '').toString().trim();
          const icon = bullet.icon;
          return text ? { text, icon } : null;
        }
        return null;
      })
      .filter(Boolean);
  }, [derivedContent.hero.bullets]);

  const experienceCards = useMemo(() => {
    const cards = Array.isArray(derivedContent.experience_section.cards)
      ? derivedContent.experience_section.cards
      : [];
    return cards
      .map((card) => ({
        icon: card?.icon,
        title: (card?.title ?? '').toString().trim(),
        description: (card?.description ?? '').toString().trim()
      }))
      .filter((card) => card.title || card.description || card.icon);
  }, [derivedContent.experience_section.cards]);

  const highlightCards = useMemo(() => {
    const cards = Array.isArray(derivedContent.highlights_section.cards)
      ? derivedContent.highlights_section.cards
      : [];
    return cards
      .map((card) => ({
        icon: card?.icon,
        title: (card?.title ?? '').toString().trim(),
        description: (card?.description ?? '').toString().trim()
      }))
      .filter((card) => card.title || card.description || card.icon);
  }, [derivedContent.highlights_section.cards]);

  const managementCards = useMemo(() => {
    const cards = Array.isArray(derivedContent.management_section.cards)
      ? derivedContent.management_section.cards
      : [];
    return cards
      .map((card) => {
        const itemsRaw = Array.isArray(card?.items) ? card.items : [];
        const items = itemsRaw
          .map((item) => {
            if (typeof item === 'string') {
              const text = item.trim();
              return text ? { text, icon: undefined } : null;
            }
            if (item && typeof item === 'object') {
              const text = (item.text || item.name || '').toString().trim();
              const icon = item.icon;
              return text ? { text, icon } : null;
            }
            return null;
          })
          .filter(Boolean);
        return {
          icon: card?.icon,
          title: (card?.title ?? '').toString().trim(),
          description: (card?.description ?? '').toString().trim(),
          items
        };
      })
      .filter((card) => card.title || card.description || card.icon || card.items.length);
  }, [derivedContent.management_section.cards]);

  const ctaHighlights = useMemo(() => {
    const highlights = Array.isArray(derivedContent.cta_section.highlights)
      ? derivedContent.cta_section.highlights
      : [];
    return highlights
      .map((highlight) => ({
        icon: highlight?.icon,
        title: (highlight?.title ?? '').toString().trim(),
        description: (highlight?.description ?? '').toString().trim()
      }))
      .filter((highlight) => highlight.title || highlight.description || highlight.icon);
  }, [derivedContent.cta_section.highlights]);

  const hasHeroContent = Boolean(
    derivedContent.hero.badge ||
    derivedContent.hero.title ||
    derivedContent.hero.description ||
    derivedContent.hero.image_url ||
    heroBullets.length
  );

  const showPackagesHeader = Boolean(
    derivedContent.packages_section.title || derivedContent.packages_section.description
  );

  const hasExperienceSection = Boolean(
    derivedContent.experience_section.title ||
    derivedContent.experience_section.description ||
    experienceCards.length
  );

  const hasHighlightsSection = Boolean(
    derivedContent.highlights_section.title ||
    derivedContent.highlights_section.subtitle ||
    highlightCards.length
  );

  const hasManagementSection = Boolean(
    derivedContent.management_section.title ||
    derivedContent.management_section.description ||
    managementCards.length
  );

  const primaryButton = derivedContent.cta_section.primary_button;
  const secondaryButton = derivedContent.cta_section.secondary_button;

  const showPrimaryButton = Boolean(
    primaryButton && primaryButton.text && typeof primaryButton.link === 'string' && primaryButton.link.trim()
  );

  const showSecondaryButton = Boolean(
    secondaryButton &&
      secondaryButton.text &&
      ((secondaryButton.action === 'whatsapp' && (secondaryButton.phone || whatsappNumber)) ||
        (typeof secondaryButton.link === 'string' && secondaryButton.link.trim()))
  );

  const hasCTASection = Boolean(
    derivedContent.cta_section.title ||
    derivedContent.cta_section.description ||
    showPrimaryButton ||
    showSecondaryButton ||
    ctaHighlights.length
  );

  const decode = (str) => {
    if (!str) return str;
    return str
      .replace(/Ä±/g, 'ı')
      .replace(/Ä°/g, 'İ')
      .replace(/ÄŸ/g, 'ğ')
      .replace(/Ã¼/g, 'ü')
      .replace(/Ã§/g, 'ç')
      .replace(/Ã¶/g, 'ö')
      .replace(/Ã‡/g, 'Ç')
      .replace(/Ã–/g, 'Ö')
      .replace(/Ãœ/g, 'Ü')
      .replace(/Åž/g, 'Ş')
      .replace(/ÄŸ/g, 'Ğ')
      .replace(/yÄ±l/g, 'yıl');
  };

  const fetchPackages = async () => {
    try {
      setLoading(true);
      const res = await fetch(API_URLS.RESELLER_HOSTING_PACKAGES);
      const text = await res.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch (e) {
        console.error('Non-JSON:', text);
        data = { data: [] };
      }
      if (data.success) {
        const mapped = (data.data || [])
          .map((p) => {
            let rawFeatures = Array.isArray(p.features)
              ? p.features
              : p.features
              ? JSON.parse(p.features)
              : [];
            const parsedFeatures = rawFeatures.map((f) => {
              if (typeof f === 'string') {
                return { name: decode(f), text: decode(f), included: true };
              }
              if (f && typeof f === 'object') {
                return {
                  name: decode(f.name || f.text || ''),
                  text: decode(f.text || f.name || ''),
                  included: f.included !== false
                };
              }
              return { name: decode(String(f)), text: decode(String(f)), included: true };
            });

            return {
              id: p.id,
              name: decode(p.name),
              description: decode(p.description),
              price: p.price,
              original: p.original_price || '',
              period: decode(p.period || '/ay'),
              featured: p.featured == 1,
              features: parsedFeatures,
              display_order: p.display_order || 0
            };
          })
          .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
        setPackages(mapped);
      } else {
        setPackages([]);
      }
    } catch (e) {
      console.error('Reseller packages fetch failed:', e);
      setPackages([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchFaqs = async () => {
    try {
      setFaqsLoading(true);
      const res = await fetch(API_URLS.FAQ);
      const data = await res.json();
      const categories = data.categories || [];
      const questions = data.questions || [];
      const cat =
        categories.find((c) => (c.title || '').toLowerCase() === 'bayi hosting') ||
        categories.find((c) => (c.title || '').toLowerCase() === 'reseller hosting') ||
        categories.find((c) => (c.title || '').toLowerCase().includes('bayi')) ||
        null;
      if (cat) {
        const items = questions
          .filter((q) => q.category_id === cat.id && (q.is_active == null || q.is_active === 1))
          .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
          .map((q) => ({ q: decode(q.question), a: decode(q.answer) }));
        setFaqs(items);
      } else {
        setFaqs([]);
      }
    } catch (e) {
      console.error('FAQ fetch failed:', e);
      setFaqs([]);
    } finally {
      setFaqsLoading(false);
    }
  };

  const PackageCard = ({ plan, index }) => {
    const [isHovered, setIsHovered] = useState(false);

    const normalizedFeatures = (plan.features || []).map((f) => {
      if (typeof f === 'string') {
        return { name: f, text: f, included: true };
      }
      if (f && typeof f === 'object') {
        return {
          name: f.name || f.text || '',
          text: f.text || f.name || '',
          included: f.included !== false
        };
      }
      return { name: String(f), text: String(f), included: true };
    });

    const priceNum = parsePrice(plan.price);
    const originalPriceNum = parsePrice(plan.original);
    const discount = originalPriceNum > 0 ? Math.round(((originalPriceNum - priceNum) / originalPriceNum) * 100) : 0;

    return (
      <Card
        className={`relative border-0 shadow-xl cursor-pointer group flex flex-col h-full ${
          plan.featured ? 'ring-4 ring-purple-200' : ''
        } bg-white`}
      >
        {plan.featured && (
          <div className="absolute top-0 transform -translate-x-1/2 -translate-y-1/2 z-10" style={{ left: '52%' }}>
            <Badge className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-4 py-1 text-sm font-bold" style={{ width: '150px' }}>
              <Crown className="w-4 h-4 mr-1" />
              EN POPÜLER
            </Badge>
          </div>
        )}

        <CardHeader className="space-y-4 pb-6">
          <div className="space-y-4">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-600 to-emerald-800 flex items-center justify-center">
              <Users className="w-8 h-8 text-white" />
            </div>

            <div className="space-y-2">
              <CardTitle className="text-2xl font-bold text-gray-900 group-hover:text-emerald-700 transition-colors">
                {plan.name}
              </CardTitle>
              {plan.description && (
                <p className="text-gray-600 leading-relaxed">{plan.description}</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-baseline space-x-2">
              <span className="text-4xl font-bold text-gray-900">
                {formatPriceWithoutCents(plan.price)}
              </span>
              <span className="text-gray-600">/yıllık</span>
            </div>

            {plan.original && (
              <div className="flex items-center space-x-2">
                <span className="text-lg text-gray-400 line-through">
                  {formatPriceWithoutCents(plan.original)}
                </span>
                {discount > 0 && <Badge variant="destructive">%{discount} İndirim</Badge>}
              </div>
            )}

          </div>
        </CardHeader>

        <CardContent className="space-y-6 flex flex-col flex-1">
          <div className="space-y-3">
            <h4 className="font-semibold text-gray-800">Özellikler:</h4>
            {normalizedFeatures.map((feature, idx) => {
              const isIncluded = feature.included !== false;
              const featureText =
                typeof feature === 'string' ? feature : feature?.text || feature?.name || String(feature);
              return (
                <div key={idx} className="flex items-center space-x-3">
                  {isIncluded ? (
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  ) : (
                    <XIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  )}
                  <span className={`text-sm font-medium ${isIncluded ? 'text-gray-900' : 'text-gray-400 line-through'}`}>
                    {featureText}
                  </span>
                </div>
              );
            })}
          </div>

          <div className="mt-auto">
            <Button className="w-full h-12 font-semibold bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 text-white">
              <span>PLANI SEÇ</span>
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-emerald-50">
      {hasHeroContent && (
        <section
          className="relative bg-gradient-to-br from-emerald-600 via-emerald-700 to-emerald-800 py-20 overflow-hidden"
          style={{ opacity: contentReady ? 1 : 0, transition: 'opacity 0.6s ease-in' }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div className="text-center lg:text-left">
                {derivedContent.hero.badge && (
                  <Badge className="bg-emerald-600 text-white px-4 py-2 text-lg mx-auto lg:mx-0">
                    {derivedContent.hero.badge}
                  </Badge>
                )}
                {derivedContent.hero.title && (
                  <h1 className="text-4xl md:text-5xl font-extrabold text-white leading-tight">
                    {derivedContent.hero.title}
                  </h1>
                )}
                {derivedContent.hero.description && (
                  <p className="mt-4 text-emerald-100 text-lg">{derivedContent.hero.description}</p>
                )}
                {heroBullets.length > 0 && (
                  <div className="mt-6 flex flex-wrap items-center gap-3 text-emerald-100 justify-center lg:justify-start">
                    {heroBullets.map((bullet, idx) => {
                      const BulletIcon = resolveIcon(bullet.icon, 'CheckCircle');
                      return (
                        <div key={idx} className="flex items-center text-sm md:text-base">
                          <BulletIcon className="w-5 h-5 text-emerald-300 mr-2" />
                          {bullet.text}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              {derivedContent.hero.image_url && (
                <div className="hidden lg:block">
                  <div className="rounded-2xl bg-white/10 border border-white/10 overflow-hidden">
                    <MediaDisplay
                      src={derivedContent.hero.image_url}
                      alt={derivedContent.hero.title || 'Hero Görseli'}
                      className="w-full h-full object-cover"
                      videoProps={{ controls: true }}
                      imgProps={{ loading: "lazy" }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      <section
        className="py-20 bg-white"
        style={{ opacity: contentReady ? 1 : 0, transition: 'opacity 0.6s ease-in' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {showPackagesHeader && (
            <div className="text-center mb-12">
              {derivedContent.packages_section.title && (
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                  {derivedContent.packages_section.title}
                </h2>
              )}
              {derivedContent.packages_section.description && (
                <p className="text-lg text-gray-600">
                  {derivedContent.packages_section.description}
                </p>
              )}
            </div>
          )}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <div className="rounded-full h-12 w-12 border-b-4 border-emerald-600 mx-auto mb-3"></div>
                <p className="text-gray-600">Paketler yükleniyor...</p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {(packages.length ? packages : fallbackPlans).map((plan, idx) => (
                <PackageCard key={plan.id || plan.code || idx} plan={plan} index={idx} />
              ))}
            </div>
          )}
        </div>
      </section>

      {hasExperienceSection && (
        <section
          className="py-20 bg-gray-50"
          style={{ opacity: contentReady ? 1 : 0, transition: 'opacity 0.6s ease-in' }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center flex-col-reverse lg:grid">
              {derivedContent.hero.image_url && (
                <div className="bg-gradient-to-r from-emerald-50 to-green-50 rounded-lg overflow-hidden">
                  <MediaDisplay
                    src={derivedContent.hero.image_url}
                    alt={derivedContent.hero.title || 'Bayi Hosting'}
                    className="w-full h-full object-cover"
                    videoProps={{ controls: true }}
                    imgProps={{ loading: "lazy" }}
                  />
                </div>
              )}
              <div className="text-center lg:text-left">
                {derivedContent.experience_section.title && (
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                    {derivedContent.experience_section.title}
                  </h2>
                )}
                {derivedContent.experience_section.description && (
                  <p className="text-gray-600 mb-8">
                    {derivedContent.experience_section.description}
                  </p>
                )}

                {experienceCards.length > 0 && (
                  <div className="space-y-4">
                    {experienceCards.map((card, idx) => {
                      const CardIcon = resolveIcon(card.icon, 'HardDrive');
                      return (
                        <Card key={idx} className="p-6">
                          <div className="flex items-center mb-3 justify-center lg:justify-start">
                            <CardIcon className="w-7 h-7 text-emerald-600 mr-3" />
                            {card.title && (
                              <h3 className="text-xl font-semibold text-gray-900">{card.title}</h3>
                            )}
                          </div>
                          {card.description && <p className="text-gray-600 text-center lg:text-left">{card.description}</p>}
                        </Card>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {hasHighlightsSection && (
        <section
          className="py-20 bg-gradient-to-br from-gray-800 to-gray-900 text-white"
          style={{ opacity: contentReady ? 1 : 0, transition: 'opacity 0.6s ease-in' }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {highlightCards.length > 0 && (
                <div className="hidden lg:block">
                  <div className="h-full min-h-[280px] rounded-lg bg-gradient-to-r from-emerald-700 to-green-700" />
                </div>
              )}

              <div className={`${highlightCards.length > 0 ? '' : 'lg:col-span-2'} text-center lg:text-left`}>
                {derivedContent.highlights_section.title && (
                  <h2 className="text-3xl md:text-4xl font-bold mb-4">
                    {derivedContent.highlights_section.title}
                  </h2>
                )}
                {derivedContent.highlights_section.subtitle && (
                  <p className="text-emerald-100 mb-8">
                    {derivedContent.highlights_section.subtitle}
                  </p>
                )}

                {highlightCards.length > 0 && (
                  <div className="space-y-4">
                    {highlightCards.map((card, idx) => {
                      const CardIcon = resolveIcon(card.icon, 'Settings');
                      return (
                        <Card key={idx} className="p-6 bg-white/5 border-white/10">
                          <div className="flex items-center mb-2 justify-center lg:justify-start">
                            <CardIcon className="w-6 h-6 text-emerald-300 mr-2" />
                            {card.title && (
                              <h3 className="text-xl font-semibold text-white">{card.title}</h3>
                            )}
                          </div>
                          {card.description && (
                            <p className="text-emerald-100">{card.description}</p>
                          )}
                        </Card>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {hasManagementSection && (
        <section
          className="py-20 bg-white"
          style={{ opacity: contentReady ? 1 : 0, transition: 'opacity 0.6s ease-in' }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {(derivedContent.management_section.title ||
              derivedContent.management_section.description) && (
              <div className="text-center mb-12 space-y-4">
                {derivedContent.management_section.title && (
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                    {derivedContent.management_section.title}
                  </h2>
                )}
                {derivedContent.management_section.description && (
                  <p className="text-gray-600">
                    {derivedContent.management_section.description}
                  </p>
                )}
              </div>
            )}
            {managementCards.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {managementCards.map((card, idx) => {
                  const CardIcon = resolveIcon(card.icon, 'Settings');
                  return (
                    <Card key={idx} className="p-6">
                      <div className="flex items-center mb-3">
                        <CardIcon className="w-6 h-6 text-emerald-600 mr-2" />
                        {card.title && (
                          <h3 className="text-lg font-semibold text-gray-900">{card.title}</h3>
                        )}
                      </div>
                      {card.description && (
                        <p className="text-sm text-gray-600 mb-4">{card.description}</p>
                      )}
                      {card.items.length > 0 && (
                        <ul className="text-sm text-gray-700 space-y-2">
                          {card.items.map((item, itemIdx) => {
                            const ItemIcon = resolveIcon(item.icon, 'CheckCircle');
                            return (
                              <li key={itemIdx} className="flex items-center">
                                <ItemIcon className="w-4 h-4 text-emerald-500 mr-2" />
                                {item.text}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      )}

      <section
        className="py-16 bg-gray-50"
        style={{ opacity: contentReady ? 1 : 0, transition: 'opacity 0.6s ease-in' }}
      >
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">
              Sıkça Sorulan <span className="text-emerald-600">Sorular</span>
            </h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              Bayi Hosting ile ilgili merak ettiğiniz soruların cevaplarını burada bulabilirsiniz
            </p>
          </div>
          <div className="max-w-4xl mx-auto">
            {faqsLoading ? (
              <div className="text-center p-8 bg-white rounded-xl">
                <p className="text-gray-500">Yükleniyor...</p>
              </div>
            ) : (
              <Accordion type="single" collapsible className="w-full">
                {(faqs.length ? faqs : fallbackFaqs).map((item, i) => (
                  <AccordionItem key={i} value={`item-${i}`}>
                    <AccordionTrigger className="text-left">
                      <div className="flex items-center">
                        <HelpCircle className="w-5 h-5 mr-3 text-emerald-600" />
                        {item.q}
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-600">{item.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            )}
          </div>
        </div>
      </section>

      {hasCTASection && (
        <section
          className="py-20 bg-gradient-to-br from-emerald-600 via-emerald-700 to-emerald-800 text-white"
          style={{ opacity: contentReady ? 1 : 0, transition: 'opacity 0.6s ease-in' }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              {derivedContent.cta_section.title && (
                <h2 className="text-4xl font-bold mb-4">{derivedContent.cta_section.title}</h2>
              )}
              {derivedContent.cta_section.description && (
                <p className="text-xl text-emerald-100 mb-8 max-w-3xl mx-auto">
                  {derivedContent.cta_section.description}
                </p>
              )}
              {(showPrimaryButton || showSecondaryButton) && (
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                  {showPrimaryButton && (() => {
                    const PrimaryIcon = resolveIcon(primaryButton.icon, 'Mail');
                    const link = primaryButton.link.trim();
                    return (
                      <a
                        href={link}
                        target={link.startsWith('/') ? undefined : '_blank'}
                        rel={link.startsWith('/') ? undefined : 'noopener noreferrer'}
                        className="inline-flex items-center px-8 py-4 bg-white text-emerald-700 rounded-lg hover:bg-gray-100 font-semibold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
                      >
                        <PrimaryIcon className="w-5 h-5 mr-2" />
                        {primaryButton.text}
                      </a>
                    );
                  })()}
                  {showSecondaryButton && (() => {
                    const SecondaryIcon = resolveIcon(secondaryButton.icon, 'MessageCircle');
                    const isWhatsapp = secondaryButton.action === 'whatsapp';
                    const phone = secondaryButton.phone || whatsappNumber;
                    const message = (secondaryButton.prefill_message || '').trim();
                    const query = isWhatsapp
                      ? `https://api.whatsapp.com/send/?phone=${encodeURIComponent(phone)}${
                          message ? `&text=${encodeURIComponent(message)}` : ''
                        }`
                      : secondaryButton.link.trim();
                    const isExternal = isWhatsapp || !query.startsWith('/');
                    return (
                      <a
                        href={query}
                        target={isExternal ? '_blank' : undefined}
                        rel={isExternal ? 'noopener noreferrer' : undefined}
                        className="inline-flex items-center px-8 py-4 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold text-lg transition-all duration-300 transform hover:scale-105 border-2 border-white shadow-lg"
                      >
                        <SecondaryIcon className="w-5 h-5 mr-2" />
                        {secondaryButton.text}
                      </a>
                    );
                  })()}
                </div>
              )}
              {ctaHighlights.length > 0 && (
                <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
                  {ctaHighlights.map((highlight, idx) => {
                    const HighlightIcon = resolveIcon(highlight.icon, 'Clock');
                    return (
                      <div key={idx} className="bg-white/10 backdrop-blur-sm rounded-lg p-6 text-center space-y-2">
                        <HighlightIcon className="w-12 h-12 mx-auto mb-4" />
                        {highlight.title && <h3 className="font-semibold text-lg">{highlight.title}</h3>}
                        {highlight.description && (
                          <p className="text-emerald-100">{highlight.description}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default ResellerHostingPage;


